/**
 * Managed-by filter dropdown: unset, everyone, unmanaged only, or an explicit manager-id list.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, X, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { Input } from '@/components/forms/Input';
import { getCoordinators } from '@/services/users.service';
import { useAuthStore } from '@/stores/authStore';
import { roleMatches } from '@/utils/role-label-utils';

/** Filter: unset, everyone, unmanaged only, or an explicit manager-id list. */
export type ManagedByValue = undefined | 'all' | 'unmanaged' | string[];

/** Selected-value chip; remove does not toggle the dropdown. */
const Tag: React.FC<{ label: string; onRemove: (e: React.MouseEvent) => void }> = ({
  label,
  onRemove,
}) => (
  <div className="modern-tag modern-tag-primary">
    <span className="modern-tag-text truncate max-w-[120px]">{label}</span>
    <button
      type="button"
      className="modern-tag-remove-btn"
      onClick={(e) => {
        e.stopPropagation();
        onRemove(e);
      }}
      aria-label={`Retirer ${label}`}
    >
      <X className="h-3 w-3" />
    </button>
  </div>
);

/** Keyboard-accessible checkbox row for the managed-by dropdown. */
const DropdownItem: React.FC<{
  label: string;
  checked: boolean;
  onClick: () => void;
  indeterminate?: boolean;
}> = ({ label, checked, onClick, indeterminate = false }) => (
  <div
    role="menuitemcheckbox"
    aria-checked={indeterminate ? 'mixed' : checked}
    tabIndex={0}
    className={cn(
      'modern-dropdown-item cursor-pointer',
      checked && 'modern-dropdown-item-selected',
    )}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
  >
    <Checkbox checked={indeterminate ? 'indeterminate' : checked} className="mr-3 shrink-0" />
    <span className="flex-1 text-left text-sm">{label}</span>
  </div>
);

/** Props for {@link ManagedByMultiSelect}. */
interface ManagedByMultiSelectProps {
  value: ManagedByValue;
  onChange: (value: ManagedByValue) => void;
  inputSize?: 'sm' | 'md' | 'lg';
}

/** Multi-select of managers, plus unmanaged / everyone shortcuts. */
export const ManagedByMultiSelect: React.FC<ManagedByMultiSelectProps> = ({
  value,
  onChange,
  inputSize = 'sm',
}) => {
  const { user, roles } = useAuthStore();
  const currentUserId = user?.id;
  const isCoordinatorRemote = roles.some((r) => roleMatches(r.code, 'coordinator', 'remote'));

  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['managed-by-coordinators', searchQuery],
    queryFn: () =>
      getCoordinators({
        search: searchQuery || undefined,
        roleCodes: ['coordinator_remote'],
        pageSize: 100,
      }),
  });

  /** Unfiltered coordinator list — used to compute "all minus X" while search is active. */
  const { data: allData } = useQuery({
    queryKey: ['managed-by-coordinators', ''],
    queryFn: () =>
      getCoordinators({
        roleCodes: ['coordinator_remote'],
        pageSize: 100,
      }),
  });

  const coordinators = data?.data ?? [];
  const allCoordinators = allData?.data ?? [];

  /** Search-filtered coordinators; current user first when the viewer is a remote coordinator. */
  const sortedCoordinators = React.useMemo(() => {
    return [...coordinators].sort((a, b) => {
      if (isCoordinatorRemote) {
        if (a.id === currentUserId) return -1;
        if (b.id === currentUserId) return 1;
      }
      const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim().toLowerCase();
      const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [coordinators, currentUserId, isCoordinatorRemote]);

  React.useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', onClickOutside);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isOpen]);

  const isCoordChecked = (id: string): boolean => {
    if (value === 'all') return true;
    if (Array.isArray(value)) return value.includes(id);
    return false;
  };

  /** Toggle the exclusive `'all'` sentinel. */
  const handleClickAll = () => {
    onChange(value === 'all' ? undefined : 'all');
  };

  /** Toggle the exclusive `'unmanaged'` sentinel. */
  const handleClickUnmanaged = () => {
    onChange(value === 'unmanaged' ? undefined : 'unmanaged');
  };

  /** From `'all'`, deselecting one coordinator builds an explicit id list. */
  const handleClickCoord = (id: string) => {
    if (value === 'all') {
      // Deselect one from "all" → use the FULL unfiltered list, not the search-filtered one
      const remaining = allCoordinators.map((c) => c.id).filter((cid) => cid !== id);
      onChange(remaining.length > 0 ? remaining : undefined);
    } else if (Array.isArray(value)) {
      const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
      onChange(next.length > 0 ? next : undefined);
    } else {
      // value === undefined or 'unmanaged' → start fresh with this coordinator
      onChange([id]);
    }
  };

  const renderTriggerContent = () => {
    if (value === undefined) {
      return <span className="text-muted-foreground/60">Filtrer par gestionnaire</span>;
    }
    if (value === 'all') {
      return <Tag label="Tous" onRemove={() => onChange(undefined)} />;
    }
    if (value === 'unmanaged') {
      return <Tag label="Non géré" onRemove={() => onChange(undefined)} />;
    }

    // string[] — coordinator tags: always show only the first + "+N more" badge
    const allIds = value;
    const firstCoord = sortedCoordinators.find((c) => allIds.includes(c.id));
    const hiddenCount = firstCoord ? allIds.length - 1 : allIds.length;

    return (
      <>
        {firstCoord && (
          <Tag
            key={firstCoord.id}
            label={
              `${firstCoord.firstName ?? ''} ${firstCoord.lastName ?? ''}`.trim() ||
              firstCoord.email ||
              firstCoord.id
            }
            onRemove={(e) => {
              e.stopPropagation();
              const next = allIds.filter((id) => id !== firstCoord.id);
              onChange(next.length > 0 ? next : undefined);
            }}
          />
        )}
        {hiddenCount > 0 && (
          <span className="modern-tag modern-tag-secondary shrink-0 cursor-pointer">
            +{hiddenCount}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsOpen(false);
        }}
        className={cn(
          'inline-flex h-auto w-full items-center justify-between rounded-xl border-[1.5px] px-3 gap-2 text-left',
          inputSize === 'sm' ? 'min-h-9 py-1.5 text-sm' : 'min-h-11 py-2 text-base',
          'ring-offset-background transition-all duration-200 cursor-pointer select-none',
          'focus:outline-none focus:ring-0',
          'border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
          'bg-[rgb(var(--card-rgb)/0.3)]',
          'hover:border-[var(--color-1-nuance-2)] hover:shadow-sm',
          isOpen && 'border-[var(--color-1-nuance-1)]',
        )}
      >
        <div className="flex flex-nowrap gap-1.5 items-center flex-1 min-w-0">
          {renderTriggerContent()}
        </div>
        {Array.isArray(value) && value.length > 1 && (
          <button
            type="button"
            aria-label="Effacer le filtre"
            onClick={(e) => {
              e.stopPropagation();
              onChange(undefined);
            }}
            className="shrink-0 rounded-full p-0.5 text-[var(--color-2-nuance-1)] hover:text-[var(--color-2-nuance-2)] hover:bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={cn(
            'h-5 w-5 shrink-0 transition-transform duration-200 text-[var(--color-1-nuance-1)]',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div className="modern-dropdown top-full mt-2" role="menu">
          <div className="px-3 py-2 border-b border-border">
            <div className="relative">
              <Input
                ref={searchRef}
                inputSize="sm"
                icon={Search}
                iconPosition="left"
                placeholder="Rechercher un coordinateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className={searchQuery ? 'pr-7' : ''}
              />
              {searchQuery && (
                <button
                  type="button"
                  aria-label="Effacer la recherche"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery('');
                    searchRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-2-nuance-1)] hover:text-[var(--color-2-nuance-2)] transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          <DropdownItem label="Tous" checked={value === 'all'} onClick={handleClickAll} />
          <DropdownItem
            label="Non géré"
            checked={value === 'unmanaged'}
            onClick={handleClickUnmanaged}
          />

          <div className="h-px bg-border my-1 mx-2" />

          {isLoading && (
            <div className="px-3 py-3 flex items-center justify-center gap-2 text-sm text-[var(--color-2-nuance-1)]">
              <Spinner size="sm" />
              Chargement...
            </div>
          )}
          {!isLoading && sortedCoordinators.length === 0 && (
            <div className="px-3 py-3 text-center text-sm text-muted-foreground">
              Aucun coordinateur trouvé
            </div>
          )}
          {!isLoading &&
            sortedCoordinators.map((coord) => {
              const fullName = `${coord.firstName ?? ''} ${coord.lastName ?? ''}`.trim();
              const name = fullName || coord.email || coord.id;
              const label =
                isCoordinatorRemote && coord.id === currentUserId ? `${name} (Moi)` : name;
              return (
                <DropdownItem
                  key={coord.id}
                  label={label}
                  checked={isCoordChecked(coord.id)}
                  onClick={() => handleClickCoord(coord.id)}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};
